import { twMerge } from "tailwind-merge";
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  H3,
  H5,
  H6,
  InputField,
  InputNote,
  InputOption,
  InputSelect,
  InputTextArea,
  InputLengthIndicator,
} from "@sellar-io/components";
import {
  SupplierRelationshipActivityCreateRequestType,
  SupplierRelationshipActivityListElementResponseType,
  SupplierRelationshipGetResponseType,
} from "@sellar-io/api-v2/types";
import dayjs from "dayjs";

import relativeTime from "dayjs/plugin/relativeTime";
import { useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { Icon, ProfilePicture, SpinnerLegacy } from "../../../components";
import { InfiniteList } from "../../../components/infiniteLoading/InfiniteList";
import {
  Dialog,
  DialogNavigation,
  DialogContent,
} from "../../../components/modal/Dialog";
import { useEvents } from "../../../hooks";
import { useSupplierRelationshipActivityCreate } from "../../../hooks/api/v2/supplierRelationshipActivities/useSupplierRelationshipActivityCreate";
import { useSupplierRelationshipActivityList } from "../../../hooks/api/v2/supplierRelationshipActivities/useSupplierRelationshipActivityList";
import { useSellarCurrentBusiness } from "../../../hooks/useSellarCurrentBusiness.shared";

dayjs.extend(relativeTime);

export const CustomerActivity = ({
  connection,
}: {
  connection: SupplierRelationshipGetResponseType;
}) => {
  const { track } = useEvents();
  const supplierRelationshipActivityListQuery =
    useSupplierRelationshipActivityList({
      query: [
        {
          type: "SupplierRetailerConnectionId",
          modifier: "EQUALS",
          value: connection.id,
        },
      ],
      sort: [{ type: "createdAt", direction: "desc" }],
      limit: 10,
    });

  const connectionActivityCreateMutation =
    useSupplierRelationshipActivityCreate();

  const [createActivityModalActive, setCreateActivityModalActive] =
    useState(false);

  return (
    <>
      <Card className="bg-white">
        <CardHeader
          className={twMerge("bg-white", "flex items-center justify-between")}
        >
          <H3>Sales activity</H3>
          <Button
            color={"secondary"}
            onClick={() => setCreateActivityModalActive(true)}
          >
            <Icon icon={"PLUS"} />
            Activity
          </Button>
        </CardHeader>
        <CardContent className={"flex flex-col gap-1"}>
          {supplierRelationshipActivityListQuery.isLoading ? (
            <SpinnerLegacy />
          ) : (
            <InfiniteList query={supplierRelationshipActivityListQuery}>
              {supplierRelationshipActivityListQuery.data?.pages.map((page) =>
                page.map((activity) => (
                  <ActivityCard
                    key={activity.id}
                    activity={activity}
                    buyer={connection.Retailer}
                  />
                )),
              )}
            </InfiniteList>
          )}
        </CardContent>
      </Card>
      <ActivityCreateModal
        active={createActivityModalActive}
        onDismiss={() => setCreateActivityModalActive(false)}
        loading={connectionActivityCreateMutation.isPending}
        onSubmit={(data) => {
          connectionActivityCreateMutation.mutate(
            {
              ...data,
              SupplierRetailerConnectionId: connection.id,
            },
            {
              onSuccess: () => {
                setCreateActivityModalActive(false);
                track("Sales Activity Logged", {
                  SupplierId: connection.SupplierId,
                  RetailerId: connection.BuyerId ?? 0,
                  activityType: data.type,
                  notes: data.message,
                });
              },
            },
          );
        }}
      />
    </>
  );
};

const ActivityCard = ({
  activity,
  buyer,
}: {
  activity: SupplierRelationshipActivityListElementResponseType;
  buyer: SupplierRelationshipGetResponseType["Retailer"];
}) => {
  const generateCaption = (): string => {
    const username = activity.BusinessUser.User.name ?? "An unknown user";

    switch (activity.type) {
      case "phoneCall":
        return `📞 ${username} logged a
            phone call with ${buyer.Business.name}`;
      case "email":
        return `📧 ${username} logged an email with ${buyer.Business.name}`;
      case "visit":
        return `📍 ${username} logged a visit to ${buyer.Business.name}`;
      case "note":
        return `📝 ${username} logged a note for ${buyer.Business.name}`;
      case "terms":
        return `🤝 ${username} assigned terms for ${buyer.Business.name}`;
      default:
        throw new Error("Unknown activity type");
    }
  };

  return (
    <div className={"flex gap-4"}>
      <div className={"flex flex-col items-center gap-2"}>
        <ProfilePicture
          name={activity.BusinessUser.User.name ?? "?"}
          size={"small"}
        />
        <div className={"h-full w-px bg-zinc-200"} />
      </div>
      <div className={"flex flex-col gap-4"}>
        <div>
          <H5>{generateCaption()}</H5>
          <H6>
            {dayjs(activity.createdAt).format("DD/MM/YY")} (
            {dayjs().to(dayjs(activity.createdAt))})
          </H6>
        </div>
        {activity.message && <p className={"pb-8"}>{activity.message}</p>}
      </div>
    </div>
  );
};

const ActivityCreateModal = ({
  active,
  onDismiss,
  onSubmit,
  loading,
}: {
  active: boolean;
  onDismiss: () => void;
  onSubmit: (
    data: Pick<
      SupplierRelationshipActivityCreateRequestType,
      "message" | "type"
    >,
  ) => void;
  loading: boolean;
}) => {
  const { business } = useSellarCurrentBusiness();

  if (!business) throw new Error("Cannot find business");

  const { register, handleSubmit, control } = useForm<
    Pick<SupplierRelationshipActivityCreateRequestType, "message" | "type">
  >({
    shouldUseNativeValidation: true,
  });
  const message = useWatch({
    control,
    name: "message",
  });

  return (
    <Dialog active={active} onDismiss={onDismiss}>
      <DialogNavigation
        title={"Log activity"}
        leftIcon={"CHEVRON_DOWN"}
        onClickLeftIcon={onDismiss}
      />
      <DialogContent>
        {loading ? (
          <SpinnerLegacy />
        ) : (
          <form
            onSubmit={handleSubmit(onSubmit)}
            className={"flex flex-col gap-4"}
          >
            <InputField label={"Activity type"}>
              <InputSelect {...register("type", { required: true })}>
                <InputOption value="" disabled hidden>
                  Choose...
                </InputOption>
                <option value={"phoneCall"}>Phone call</option>
                <option value={"email"}>Email</option>
                <option value={"visit"}>Visit</option>
                <option value={"note"}>Note</option>
              </InputSelect>
            </InputField>
            <InputField
              label={"Notes"}
              note={
                <InputNote>{`Your customer won't be able to see your notes. They are only visible to you and your colleagues at ${business.name}.`}</InputNote>
              }
            >
              <InputTextArea
                className="h-[100px] resize-none rounded-md border border-solid border-gray-200 p-2.5"
                {...register("message", { required: true, maxLength: 900 })}
              />
              <InputLengthIndicator
                maxLength={900}
                currentLength={message?.length ?? 0}
              />
            </InputField>
            <Button type={"submit"} className={"w-full"}>
              Save
            </Button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};
